import { DateTime } from "luxon";

interface CacheOptions {
  ttl?: number;
  forceRefresh?: boolean; 
  cacheKey?: string;
}

interface CacheEntry {
  data: any;
  expiry: number;
}

let CACHE_MAN_INSTANCE: CacheManager | null = null;

export class CacheManager {
    private cacheName: string;
    private defaultTTL: number;
    private cache: Cache | null = null;

    private constructor(cacheName = 'http-yenta-cache', defaultTTL = DateTime.now().plus({ days: 30 }).toMillis()) { // 30 days default TTL
      this.cacheName = cacheName;
      this.defaultTTL = defaultTTL;
    }

    public static Instance() {
        if (!CACHE_MAN_INSTANCE) {
            CACHE_MAN_INSTANCE = new CacheManager();
            CACHE_MAN_INSTANCE.init()
                .then(() => console.log('Cache initialized'))
                .catch(console.error);
        }
        return CACHE_MAN_INSTANCE;
    }
  
    private async init(): Promise<void> {
      // Open the cache
      this.cache = await caches.open(this.cacheName);
    }

    public static async Fetch(request: Request, options: CacheOptions = {}): Promise<Response> {
      const cacheMan = CacheManager.Instance();
      return await cacheMan.fetch(request, options);
    }
  
    public async fetch(request: Request, options: CacheOptions = {}): Promise<Response> {
      if (!this.cache) {
        // FALLBACK safe guard after trying to initialize
        try {
            console.log('attempting to initializing cache');
            await this.init();
        } catch (error) {
            console.error('Error initializing cache', error);
            console.warn('FALLBACK to fetch');
            return await fetch(request);
        }
      }

      const {
        ttl = this.defaultTTL,
        forceRefresh = false,
        cacheKey = request.url
      } = options;
  
      // If force refresh is true, skip cache
      if (forceRefresh) {
        return this._fetchAndCache(request, cacheKey, ttl);
      }
  
      // Try to get from cache first
      const cachedResponse = await this.cache!.match(cacheKey);
      
      if (cachedResponse) {
        // const cachedData = await cachedResponse.json() as CacheEntry;
        
        // // Check if cache is still valid
        // if (Date.now() < cachedData.expiry) {
        //   return new Response(cachedData.data);
        // }
        return cachedResponse;
      }
  
      // If not in cache or expired, fetch and cache
      return this._fetchAndCache(request, cacheKey, ttl);
    }
  
    private async _fetchAndCache(request: Request, cacheKey: string, ttl: number): Promise<Response> {
      if (!this.cache) {
        throw new Error('Cache not initialized');
      }

      const response = await fetch(request);
      const data = await response.blob();
  
      // Create cache entry with expiry
      // const cacheEntry: CacheEntry = {
      //   data,
      //   expiry: Date.now() + ttl
      // };
  
      // Store in cache
      await this.cache.put(
        cacheKey,
        new Response(data)
      );
  
      return new Response(data);
    }
  
    async delete(cacheKey: string) {
      if (!this.cache) {
        throw new Error('Cache not initialized');
      }
      await this.cache.delete(cacheKey);
    }
  
    async clear() {
      if (!this.cache) {
        throw new Error('Cache not initialized');
      }
      await this.cache.delete(this.cacheName);
    }
  
    async keys() {
      if (!this.cache) {
        throw new Error('Cache not initialized');
      }
      return await this.cache.keys();
    }
  }